import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { AlertTriangle, ChevronDown, MoreVertical } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { useMutation } from '../../Shared/api';
import { Modal } from '../../Shared/Modal/Modal';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { User } from '../../Shared/types';
import { Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import DropdownMenu from '../../Shared/UI/DropdownMenu';
import { Icon } from '../../Shared/UI/Icon';
import { NameBadge } from '../../Shared/UI/NameBadge';
import { useRouter } from '../router';
import { AuthenticatedUser, SharedProps } from '../types';
import Container from '../UI/Container';
import Header from '../UI/Header';
import HeaderTitle from '../UI/HeaderTitle';
import Layout from '../UI/Layout';
import AddUserForm from './AddUserForm';

export interface UsersPageProps extends SharedProps {
  users: (User & { admin: boolean; deletable: boolean; })[];
}

export default function UsersPage({
  users,
  auth: { user: authenticatedUser },
}: UsersPageProps) {
  const { t } = useTranslation();

  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Header>
        <Container size="md">
          <div className="flex items-center justify-between w-full">
            <HeaderTitle>
              {t('backend:users')}
            </HeaderTitle>
            {authenticatedUser.admin && (
              <Button onClick={() => setShowModal(true)}>
                {t('backend:add_user')}
              </Button>
            )}
            <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="sm">
              <AddUserForm onSuccess={() => setShowModal(false)} />
            </Modal>
          </div>
        </Container>
      </Header>

      <Container size="md" className="space-y-6">
        <div className="space-y-2">
          {users.map((user) => (
            <UserCard user={user} authenticatedUser={authenticatedUser} key={user.id} />
          ))}
        </div>
      </Container>
    </>
  );
}

function UserCard({
  user,
  authenticatedUser,
}: {
  user: User & { admin: boolean; deletable: boolean; };
  authenticatedUser: AuthenticatedUser;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const [editUserRole, { loading: editing }] = useMutation(routes.edit_user_role(user.id), {
    onSuccess: () => {
      notifier.notify(t('backend:user_edited'));
    },
    onError: () => {
      notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
    },
  });

  const [deleteUser, { loading: deleting }] = useMutation(routes.delete_user(user.id), {
    onSuccess: () => {
      notifier.notify(t('backend:user_removed'));
    },
    onError: () => {
      notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
    },
  });

  return (
    <Card spacing="xs" key={user.id}>
      <div className="grid grid-cols-[2.5rem_1fr_40px] gap-4 items-center leading-tight">
        <div>
          <NameBadge name={user.first_name} size="lg" />
        </div>
        <div>
          <strong>
            {user.full_name}
          </strong>
          {user.admin && (
            <>
              {' '}
              <span className="inline-block bg-slate-150 px-1 text-sm -my-0.5">
                {t('backend:admin')}
              </span>
            </>
          )}
          <br />
          {user.email}
        </div>
        <div className="text-right">
          {authenticatedUser.admin && (
            <DropdownMenu
              trigger={({ onClick, isOpen }) => (
                <Button
                  onClick={onClick}
                  variant="tertiary"
                  iconButton
                  className={classNames(isOpen && '!bg-slate-200 !border-slate-200')}
                  title={t('backend:options')}
                >
                  <Icon>
                    <MoreVertical />
                  </Icon>
                  <Icon>
                    <ChevronDown />
                  </Icon>
                </Button>
              )}
              align="end"
              className="text-center"
            >
              {!user.admin && (
                <DropdownMenu.Item>
                  <a
                    onClick={() => editUserRole({ role: 'admin' })}
                    role="button"
                    className={classNames(editing && 'pointer-events-none opacity-50')}
                  >
                    {t('backend:make_admin')}
                  </a>
                </DropdownMenu.Item>
              )}
              {user.admin && (
                <DropdownMenu.Item>
                  <a
                    onClick={() => editUserRole({ role: 'user' })}
                    role="button"
                    className={classNames(editing && 'pointer-events-none opacity-50')}
                  >
                    {t('backend:make_user')}
                  </a>
                </DropdownMenu.Item>
              )}
              {user.deletable && (
                <DropdownMenu.Item>
                  <a
                    onClick={() => deleteUser()}
                    role="button"
                    className={classNames(deleting && 'pointer-events-none opacity-50')}
                  >
                    {t('backend:remove')}
                  </a>
                </DropdownMenu.Item>
              )}
            </DropdownMenu>
          )}
        </div>
      </div>
    </Card>
  );
}

UsersPage.layout = (page: ReactNode) => <Layout>{page}</Layout>;
